import { toRelativeDocumentPath } from "../../../api/v1";

import { CATALOGUE_REGISTRY } from "./catalogueRegistry";
import { formatTimeValue, parseTimeValue } from "./timeSlotUtils";
import {
  CatalogueDef,
  CatalogueId,
  MarketingConfigurationFormData,
  MarketingConfigurationItem,
  MarketingConfigurationPayload,
  ViewingDayItem,
  isAmenityCatalogue,
  isIconUploadCatalogue,
} from "./types";

export const DEFAULT_VIEWING_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/** Ensures every weekday is present, filling in any missing ones as inactive. */
export function mergeWithDefaultViewingDays(items: ViewingDayItem[]): ViewingDayItem[] {
  return DEFAULT_VIEWING_DAYS.map(
    (label) => items.find((item) => item.label === label) ?? { uid: crypto.randomUUID(), label, isActive: false }
  );
}

export const emptyFormValues: MarketingConfigurationFormData = {
  organization: null,
  label: "",
  startTime: null,
  endTime: null,
  order: null,
  url: "",
  isActive: true,
};

export const isCatalogueId = (value: string | null): value is CatalogueId =>
  CATALOGUE_REGISTRY.some((catalogue) => catalogue.id === value);

export function getCatalogueDef(catalogueId: CatalogueId): CatalogueDef {
  const catalogue = CATALOGUE_REGISTRY.find((entry) => entry.id === catalogueId);
  if (!catalogue) {
    throw new Error(`Unknown catalogue: ${catalogueId}`);
  }
  return catalogue;
}

export function getValueByPath(source: unknown, path: string): unknown {
  if (!source || typeof source !== "object") return undefined;

  return path.split(".").reduce<unknown>((current, key) => {
    if (!current || typeof current !== "object") return undefined;
    return (current as Record<string, unknown>)[key];
  }, source);
}

/** Builds a nested object from a dot-path, e.g. `a.b.c` + value → `{ a: { b: { c: value } } }` */
export function setValueByPath(path: string, value: unknown): Record<string, unknown> {
  const parts = path.split(".");
  const root: Record<string, unknown> = {};
  let current = root;

  for (let i = 0; i < parts.length - 1; i += 1) {
    const next: Record<string, unknown> = {};
    current[parts[i]] = next;
    current = next;
  }

  current[parts[parts.length - 1]] = value;
  return root;
}

export function toFormValues(record: MarketingConfigurationItem): Partial<MarketingConfigurationFormData> {
  return {
    label: "label" in record ? record.label : "",
    startTime: "startTime" in record ? parseTimeValue(record.startTime) : null,
    endTime: "endTime" in record ? parseTimeValue(record.endTime) : null,
    order: "order" in record ? record.order : null,
    url: "url" in record ? (record.url ?? "") : "",
    isActive: record.isActive,
  };
}

export function toPayload(catalogueId: CatalogueId, formData: MarketingConfigurationFormData): MarketingConfigurationPayload {
  const isActive = formData.isActive;

  if (isAmenityCatalogue(catalogueId)) {
    return {
      label: formData.label.trim(),
      ...(isIconUploadCatalogue(catalogueId) && formData.url
        ? { url: toRelativeDocumentPath(formData.url) }
        : {}),
      isActive,
    };
  }

  if (catalogueId === "viewingDaysConfig") {
    return {
      label: formData.label.trim(),
      isActive,
    };
  }

  if (catalogueId === "viewingTimeSlotsConfig") {
    return {
      startTime: formatTimeValue(formData.startTime),
      endTime: formatTimeValue(formData.endTime),
      isActive,
    };
  }

  return {
    label: formData.label.trim(),
    order: Number(formData.order) || 1,
    isActive,
  };
}

export function getItemLabel(item: MarketingConfigurationItem): string {
  if ("label" in item) return item.label;
  if ("startTime" in item) return `${item.startTime} - ${item.endTime}`;
  return "Item";
}

export function stripUid(item: MarketingConfigurationItem): MarketingConfigurationPayload {
  const { uid: _uid, ...rest } = item;

  // Amenity assets may arrive from GET as full CDN URLs; PATCH expects relative paths.
  if ("label" in rest && "url" in rest && typeof rest.url === "string" && rest.url) {
    return {
      ...rest,
      url: toRelativeDocumentPath(rest.url),
    } as MarketingConfigurationPayload;
  }

  return rest as MarketingConfigurationPayload;
}
